import React, { useState } from "react";
import BasicDrawer from "@components/drawer/BasicDrawer";
import useOptionDrawerStore from "@store/productDetail/useOptionDrawerStore";
import BasicButton from "@components/button/BasicButton";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { ColorSelect, SizeSelect } from "@pages/product/_components";
import { useParams } from "react-router-dom";
import useSelectedColorStore from "@store/register/useSelectedColor";
import QuantityControl from "@components/input/QuantityControl";
import Image from "@components/item/Image";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { Flex } from "antd";
import { toPrice } from "@utils/functions";

type Props = {
  colorList: any[];
  onPurchaseClick?: (optionList: any) => void;
  onCartClick?: (optionList: any) => void;
  productPrice?: any;
  productCondition?: any;
};

type FormValues = {
  product_id: number;
  option_list: {
    color_id: number;
    color_name: string;
    size_list: {
      size_id: number;
      size_name: string;
      amount: number;
      maxAmount: number;
    }[];
  }[];
};

const ChoiceOptionDrawer = ({
  colorList,
  onCartClick,
  onPurchaseClick,
  productPrice,
  productCondition,
}: Props) => {
  const { open, setOpen } = useOptionDrawerStore();
  const { t } = useTranslation();
  const [selectedColor, setSelectedColor] = useState(t("선택"));
  const { colorId } = useSelectedColorStore();
  const params = useParams();
  const theme = useTheme();
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [maxQuantityReachedModalOpen, setMaxQuantityReachedModalOpen] =
    useState(false);
  const [maxAmountMessage, setMaxAmountMessage] = useState("");
  const { getMemberSubscription } = useApiRequest();

  const method = useForm<FormValues>({
    defaultValues: {
      product_id: Number(params.productId),
      option_list: [],
    },
  });

  const { control, watch, setValue } = method;
  const { append, remove } = useFieldArray({
    control,
    name: "option_list",
  });

  const optionList = watch("option_list");

  const { data: subscribeDetail } = useQuery({
    queryKey: ["subscribeDetail"],
    queryFn: () => getMemberSubscription(),
  });

  const hasSelectedOptions = () => {
    return !(
      optionList.length === 0 ||
      (optionList.length > 0 && optionList[0].size_list.length === 0)
    );
  };

  const handleCartButtonClick = () => {
    if (!hasSelectedOptions()) {
      setWarningModalOpen(true);
      return;
    }
    if (onCartClick) {
      onCartClick(optionList);
    }
  };

  const handlePurchaseButtonClick = () => {
    if (!hasSelectedOptions()) {
      setWarningModalOpen(true);
      return;
    }

    if (onPurchaseClick) {
      onPurchaseClick(optionList);
    }
  };

  const handleQuantityChange = (
    colorIndex: number,
    sizeIndex: number,
    newAmount: number,
  ) => {
    const newOptionList = [...optionList];

    const sizeItem = newOptionList[colorIndex].size_list[sizeIndex];

    // 수량이 최대치에 도달한 경우 경고 모달을 띄움
    if (newAmount === sizeItem.maxAmount) {
      setMaxAmountMessage(
        `${t("최대 수량은")} ${sizeItem.maxAmount}${t("개입니다.")}`,
      );
      setMaxQuantityReachedModalOpen(true);
    }

    newOptionList[colorIndex].size_list[sizeIndex].amount = newAmount;
    setValue("option_list", newOptionList);
  };

  const handleRemoveSize = (colorIndex: number, sizeIndex: number) => {
    const newOptionList = [...optionList];

    // 해당 색상의 사이즈 리스트에서 특정 사이즈만 제거
    newOptionList[colorIndex].size_list.splice(sizeIndex, 1);

    // 만약 해당 색상의 모든 사이즈가 삭제되었다면 색상도 제거
    if (newOptionList[colorIndex].size_list.length === 0) {
      remove(colorIndex);
    } else {
      // 아니라면 업데이트된 옵션 리스트 설정
      setValue("option_list", newOptionList);
    }
  };
  const calculateTotalCount = () => {
    let totalCount = 0;

    optionList.forEach((colorOption) => {
      colorOption.size_list.forEach((sizeItem) => {
        totalCount += sizeItem.amount;
      });
    });

    return totalCount;
  };

  // console.log(
  //   "구독 여부",
  //   subscribeDetail?.is_subscribed,
  //   "컨디션",
  //   productCondition,
  // );

  const calculateTotalPrice = () => {
    let totalPrice = calculateTotalCount() * productPrice;

    if (subscribeDetail?.is_subscribed === 1 && productCondition === "NEW") {
      return totalPrice; // 회원 & 새상품 -> 원래 가격
    } else if (
      subscribeDetail?.is_subscribed === 0 &&
      productCondition === "NEW"
    ) {
      return totalPrice * 2; // 비회원 & 새상품 -> 두 배 가격
    } else if (productCondition !== "NEW") {
      return totalPrice * 2; // 중고 상품 -> 무조건 두 배 가격
    }

    return totalPrice;
  };

  return (
    <BasicDrawer open={open} setOpen={setOpen} padding="0">
      <FormProvider {...method}>
        <form>
          <Container>
            <InputContainer>
              <ColorSelect
                name="option_list"
                selectedValue={selectedColor}
                setSelectedValue={setSelectedColor}
                colorList={colorList}
                append={append}
              />

              {colorId && (
                <SizeSelect
                  name={`option_list[${Math.max(
                    0,
                    optionList.findIndex((item) => item.color_id === colorId),
                  )}].size_list`}
                />
              )}
            </InputContainer>

            {/* 선택된 옵션들 렌더링 */}
            {optionList?.map((colorOption, colorIndex) =>
              colorOption.size_list.map((sizeItem, sizeIndex) => (
                <SelectedInfo
                  key={`${colorOption.color_id}-${sizeItem.size_id}-${sizeIndex}`}>
                  <div>
                    {colorOption.color_name}/ {sizeItem.size_name}
                  </div>

                  <QuantityBox>
                    <QuantityControl
                      value={sizeItem.amount}
                      onChange={(newAmount) =>
                        handleQuantityChange(colorIndex, sizeIndex, newAmount)
                      }
                      min={1}
                      max={sizeItem.maxAmount}
                      backgroundColor="white"
                    />

                    <CloseButton
                      onClick={() => handleRemoveSize(colorIndex, sizeIndex)}>
                      <Image
                        src={theme.image.close_round.default}
                        width="1.6rem"
                        height="1.6rem"
                      />
                    </CloseButton>
                  </QuantityBox>
                </SelectedInfo>
              )),
            )}
            <Flex justify="space-between">
              {hasSelectedOptions() && calculateTotalCount() > 0 && (
                <>
                  <ProductCount>
                    {t("총")}{" "}
                    {calculateTotalCount() > 0 && calculateTotalCount()}{" "}
                    {t("개의 상품")}
                  </ProductCount>

                  <ProductTotalPrice>
                    {t("총 금액")} <span>{toPrice(calculateTotalPrice())}</span>
                  </ProductTotalPrice>
                </>
              )}
            </Flex>

            <FooterContainer>
              <BasicButton
                title="장바구니 넣기"
                width="100%"
                height="100%"
                outlined
                onClick={handleCartButtonClick}
              />
              <BasicButton
                title="구매하기"
                width="100%"
                height="100%"
                onClick={handlePurchaseButtonClick}
              />
            </FooterContainer>
          </Container>
        </form>
      </FormProvider>

      {/* 최대 수량 도달 경고 모달 */}
      <BasicModal
        open={maxQuantityReachedModalOpen}
        setOpen={setMaxQuantityReachedModalOpen}
        activeEvent={() => setMaxQuantityReachedModalOpen(false)}
        activeName="확인"
        notice>
        <ModalContent mainMessage={maxAmountMessage} />
      </BasicModal>

      {/* 경고 모달 */}
      <BasicModal
        open={warningModalOpen}
        setOpen={setWarningModalOpen}
        activeEvent={() => {
          setWarningModalOpen(false);
        }}
        activeName="확인"
        closeEvent={() => setWarningModalOpen(false)}
        notice>
        <ModalContent mainMessage="옵션을 선택해주세요." />
      </BasicModal>
    </BasicDrawer>
  );
};

export default ChoiceOptionDrawer;

const InputContainer = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.2rem",
  width: "100%",
  height: "100%",
  // padding: "1.4rem 0",
}));

const FooterContainer = styled.footer(({ theme }) => ({
  display: "flex",
  alignItem: "center",
  justifyContent: "space-between",
  gap: "2rem",
  // gap: "1.5rem",
  width: "100%",
  maxWidth: "560px",
  backgroundColor: theme.color.white,
  textAlign: "center",
  zIndex: 2,
  borderTop: `1px solid ${theme.color.border_gray}`,
  padding: "1.4rem 0",
}));

const SelectedInfo = styled.div(({ theme }) => ({
  padding: "1.5rem",
  backgroundColor: theme.color.border_gray,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "1rem",
  fontSize: "1.4rem",
  color: theme.color.black,
  height: "4.8rem",
}));

const QuantityBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "end",
  alignItems: "center",
  gap: "1rem",
}));

const CloseButton = styled.button({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Container = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
}));

const ProductCount = styled.div(({ theme }) => ({
  color: theme.color.light_gray,
}));

const ProductTotalPrice = styled.div(({ theme }) => ({
  "& > span": {
    fontWeight: "bold",
  },
}));
